import './GroupInProgressMoneybox.component.scss';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Field, reduxForm, reset, change, untouch } from 'redux-form';

import { socialProjectsLink } from '../../../assets/constants/external-links';
import { ERoutes } from '../../../assets/constants/routes';
import { EFormName } from '../../../config/form-names.config';
import {
  EAdminRefoundTypes,
  EGroupStateKeys,
  ERefound,
  IGroupMoneybox,
  TAdminRefoundTypes,
  TRefound,
} from '../../../domain/group.interface';
import {
  closeGroupMoneybox,
  loadGroupMoneybox,
  resetGroup,
} from '../../../store/grouplist/grouplist.actions';
import { makeSelectGroupMoneybox } from '../../../store/grouplist/grouplist.selectors';
import {
  formatCurrencyToNumber,
  formatNumberToCurrency,
  normalizeCurrency,
} from '../../../utils/FormNormalizers';
import {
  isRefound,
  isValidIban,
  isValidPhoneNumber,
  required,
} from '../../../utils/FormValidators';
import { replacePathParams } from '../../../utils/NavigateWithParams';
import { InputFieldComponent, PiggyComponent } from '../../atoms/atoms.index';
import { isValidDocument } from '../../../utils/FormValidators';
import {
  NavbarComponent,
  NavigationMenuComponent,
} from '../../organisms/organisms.index';

interface IOngMessageComponentProps {
  totalAmount: number;
  excessAmount: number;
}
interface IMessageComponentProps extends IOngMessageComponentProps {
  amountReturnType: TRefound;
}

function OngMessageComponent(props: IOngMessageComponentProps) {
  const { totalAmount, excessAmount } = props;
  const [refoundStatus, setRefoundStatus] = useState(0);

  useEffect(() => {
    if (excessAmount === 0) {
      setRefoundStatus(1);
    } else {
      setRefoundStatus(2);
    }
    // eslint-disable-next-line
  }, [props]);

  return (
    <React.Fragment>
      {refoundStatus > 0 && (
        <p className='text-secondary font-weight-light'>
          <FormattedMessage
            id='pages.group-in-progress.moneytransfer.money_to_admin'
            values={{
              value: (
                <span className='text-primary'>
                  {formatNumberToCurrency(totalAmount - excessAmount)}
                </span>
              ),
            }}
          />
        </p>
      )}
      {refoundStatus > 1 && excessAmount > 0 && (
        <p className='text-secondary font-weight-light'>
          <FormattedMessage
            id='pages.group-in-progress.moneytransfer.money_to_ong'
            values={{
              value: (
                <span className='text-primary'>
                  {formatNumberToCurrency(excessAmount)}
                </span>
              ),
            }}
          />
        </p>
      )}
    </React.Fragment>
  );
}

function MessageComponent(props: IMessageComponentProps) {
  const { amountReturnType, totalAmount, excessAmount } = props;

  switch (amountReturnType) {
    case ERefound.TO_ADMIN:
      return (
        <div>
          <p className='text-primary font-weight-light'>
            <FormattedMessage id='pages.group-in-progress.moneytransfer.to_admin' />
          </p>
          <p className='text-secondary font-weight-light'>
            <FormattedMessage
              id='pages.group-in-progress.moneytransfer.money_to_admin'
              values={{
                value: (
                  <span className='text-primary'>
                    {formatNumberToCurrency(totalAmount)}
                  </span>
                ),
              }}
            />
          </p>
        </div>
      );
    case ERefound.TO_ONG:
      return <OngMessageComponent {...{ totalAmount, excessAmount }} />;
    default:
      return <div></div>;
  }
}

function GroupInProgressMoneyboxComponent(props: any) {
  const { handleSubmit, pristine, submitting } = props;
  const groupId = props.match.params.groupId;
  const dispatch = useDispatch();
  const groupMoneybox: IGroupMoneybox = useSelector(makeSelectGroupMoneybox());
  const {
    amountRaisedByLiliApp,
    amountRaised: totalAmount,
    amountReturnType,
  } = groupMoneybox;

  const [refoundType, setRefoundType] = useState<
    TAdminRefoundTypes | undefined
  >(undefined);

  const [excessAmount, setExcessAmount] = useState<string | undefined>('');

  useEffect(() => {
    dispatch(loadGroupMoneybox(groupId));
    return () => {
      dispatch(resetGroup(EGroupStateKeys.groupMoneybox));
    };
    // eslint-disable-next-line
  }, []);

  function handleSetRefoundType(selectedRefoundType: TAdminRefoundTypes) {
    setRefoundType(selectedRefoundType);
    dispatch(
      change(EFormName.CLOSE_GROUP_MONEYBOX, EAdminRefoundTypes.bizum, null),
    );
    dispatch(
      change(EFormName.CLOSE_GROUP_MONEYBOX, EAdminRefoundTypes.iban, null),
    );
    dispatch(untouch(EFormName.CLOSE_GROUP_MONEYBOX, EAdminRefoundTypes.bizum));
    dispatch(untouch(EFormName.CLOSE_GROUP_MONEYBOX, EAdminRefoundTypes.iban));
  }

  function handleFormReset(e: any) {
    e.preventDefault();
    dispatch(reset(EFormName.CLOSE_GROUP_MONEYBOX));
    props.history.goBack();
  }

  function formSubmit(values: any) {
    const formattedValues = {
      ...values,
      socialProjectAmount: !_.isNil(excessAmount)
        ? formatCurrencyToNumber(excessAmount)
        : 0,
    };
    dispatch(closeGroupMoneybox(formattedValues, groupId));
  }

  return (
    <div className='page-container group-in-progress-container'>
      <NavbarComponent />
      <NavigationMenuComponent {...props} />
      <div className='title-container'>
        <div className='title-progress-bar'></div>
        <h2 className='in-progress-title'>
          <FormattedMessage id='pages.group-in-progress.title' />
        </h2>
      </div>
      <div className='content-wrapper'>
        <div className='active-group-admin-form'>
          <form onSubmit={handleSubmit(formSubmit)}>
            <div>
              <h4 className='subtitle'>
                <FormattedMessage id='pages.group-in-progress.in_piggy.title' />
              </h4>

              <div className='md-card d-flex align-items-center flex-column text-center'>
                <PiggyComponent amount={totalAmount} />
                {!!totalAmount && (
                  <p className='mt-3'>
                    <FormattedMessage
                      id='pages.active-group.moneybox.external'
                      values={{
                        value: (
                          <span className='text-primary'>
                            {formatNumberToCurrency(amountRaisedByLiliApp)}
                          </span>
                        ),
                      }}
                    />
                  </p>
                )}
                {!_.isNil(amountReturnType) && (
                  <p className='text-center'>
                    <span>
                      <FormattedMessage id='pages.active-group.moneybox.refound' />
                      {': '}
                    </span>
                    <br />
                    <span className='text-primary font-weight-light'>
                      <FormattedMessage
                        id={`common.refound.${amountReturnType}`}
                      />
                      {amountReturnType === ERefound.TO_ONG && (
                        <a
                          href={socialProjectsLink.link}
                          aria-label={socialProjectsLink.name}
                          target='_blank'
                          rel='noopener noreferrer'
                        >
                          <FontAwesomeIcon icon='info-circle' />
                        </a>
                      )}
                    </span>
                  </p>
                )}
              </div>
            </div>
            {amountReturnType === ERefound.TO_ONG && (
              <div>
                <h4 className='subtitle'>
                  <FormattedMessage id='pages.group-in-progress.excess-amount.title' />
                </h4>
                <div className='field-container mb-3'>
                  <FormattedMessage id='pages.group-in-progress.excess-amount.placeholder'>
                    {(txt: any) => (
                      <input
                        name={'excessAmount'}
                        id='excessAmount'
                        className={`field ${
                          !_.isNil(excessAmount) &&
                          amountRaisedByLiliApp -
                            formatCurrencyToNumber(excessAmount) <
                            0
                            ? 'field-error'
                            : ''
                        }`}
                        placeholder={txt}
                        value={excessAmount}
                        onChange={(e: any) =>
                          e.target.value === '€ '
                            ? setExcessAmount('')
                            : setExcessAmount(normalizeCurrency(e.target.value))
                        }
                        autoComplete='false'
                      />
                    )}
                  </FormattedMessage>
                </div>
                <div>
                  {!_.isNil(excessAmount) &&
                    amountRaisedByLiliApp -
                      formatCurrencyToNumber(excessAmount) <
                      0 && (
                      <p className='font-weight-light text-danger'>
                        <FormattedMessage id='pages.group-in-progress.moneytransfer.error' />
                      </p>
                    )}
                  {_.isNil(excessAmount) ||
                    (!_.isNil(excessAmount) &&
                      formatCurrencyToNumber(excessAmount) === 0 && (
                        <p className='font-weight-light text-primary'>
                          <FormattedMessage
                            id={`pages.group-in-progress.moneytransfer.to_admin`}
                          />
                        </p>
                      ))}
                  {!_.isNil(excessAmount) &&
                    formatCurrencyToNumber(excessAmount) > 0 &&
                    amountRaisedByLiliApp -
                      formatCurrencyToNumber(excessAmount) >=
                      0 && (
                      <p className='font-weight-light text-primary'>
                        <FormattedMessage
                          id={`pages.group-in-progress.moneytransfer.to_ong`}
                        />
                      </p>
                    )}
                </div>
              </div>
            )}
            {_.isNil(excessAmount) ||
              (amountRaisedByLiliApp - formatCurrencyToNumber(excessAmount) >=
                0 && (
                <div>
                  <h4 className='subtitle'>
                    <FormattedMessage id='pages.group-in-progress.moneytransfer.title' />
                  </h4>
                  <MessageComponent
                    {...{
                      amountReturnType,
                      totalAmount: amountRaisedByLiliApp,
                      excessAmount: _.isNil(excessAmount)
                        ? 0
                        : formatCurrencyToNumber(excessAmount),
                    }}
                  />
                </div>
              ))}
            <div>
              <h4 className='subtitle'>
                <FormattedMessage id='pages.group-in-progress.refound.title' />
              </h4>
              <div className='field-container mb-3'>
                <FormattedMessage id='pages.group-in-progress.refound.name.placeholder'>
                  {(txt: any) => (
                    <Field
                      name='name'
                      component={InputFieldComponent}
                      validate={[required]}
                      label={txt}
                    />
                  )}
                </FormattedMessage>
              </div>
              <div className='field-container mb-2'>
                <FormattedMessage id='pages.group-in-progress.refound.documentId.placeholder'>
                  {(txt: any) => (
                    <Field
                      name='documentId'
                      component={InputFieldComponent}
                      validate={[required, isValidDocument]}
                      label={txt}
                    />
                  )}
                </FormattedMessage>
              </div>
              <div className='field-container mb-3'>
                <div className='radio-container'>
                  <label htmlFor='opt3' className='radio'>
                    <input
                      name='refound-radio'
                      onChange={() =>
                        handleSetRefoundType(EAdminRefoundTypes.iban)
                      }
                      value='iban'
                      type='radio'
                      id='opt3'
                      className='hide'
                    />
                    <span className='label'></span>
                    <FormattedMessage id='pages.group-in-progress.refound.iban.label' />
                  </label>
                  <label htmlFor='opt4' className='radio'>
                    <input
                      name='refound-radio'
                      onChange={() =>
                        handleSetRefoundType(EAdminRefoundTypes.bizum)
                      }
                      value='bizum'
                      type='radio'
                      id='opt4'
                      className='hide'
                    />
                    <span className='label'></span>
                    <FormattedMessage id='pages.group-in-progress.refound.bizum.label' />
                  </label>
                </div>
              </div>
              <div
                className={
                  refoundType === EAdminRefoundTypes.iban ? '' : 'hide'
                }
              >
                <FormattedMessage id='pages.group-in-progress.refound.iban.placeholder'>
                  {(txt: any) => (
                    <Field
                      name={EAdminRefoundTypes.iban}
                      component={InputFieldComponent}
                      validate={[isValidIban, isRefound]}
                      label={txt}
                    />
                  )}
                </FormattedMessage>
              </div>
              <div
                className={
                  refoundType === EAdminRefoundTypes.bizum ? '' : 'hide'
                }
              >
                <FormattedMessage id='pages.group-in-progress.refound.bizum.placeholder'>
                  {(txt: any) => (
                    <Field
                      name={EAdminRefoundTypes.bizum}
                      component={InputFieldComponent}
                      validate={[isValidPhoneNumber, isRefound]}
                      label={txt}
                    />
                  )}
                </FormattedMessage>
              </div>
            </div>
            <section className='form-button-container mt-6'>
              <div className='left-wfc'>
                <button
                  className='btn-lili danger'
                  onClick={(e: any) => handleFormReset(e)}
                >
                  <FormattedMessage id='common.cancel' />
                </button>
              </div>
              <div className='right-wfc'>
                <button
                  className='btn-lili'
                  type='submit'
                  disabled={
                    pristine ||
                    submitting ||
                    (!_.isNil(excessAmount) &&
                      amountRaisedByLiliApp <
                        formatCurrencyToNumber(excessAmount))
                  }
                >
                  <FormattedMessage id='common.save' />
                </button>
              </div>
            </section>
          </form>
        </div>
      </div>
      <div className='chat-button-container'>
        <Link to={replacePathParams(ERoutes.CHAT, { groupId: groupId })}>
          <button type='button' className='btn btn-secondary btn-circle'>
            <FontAwesomeIcon icon='comment-alt' size='2x' />
          </button>
        </Link>
      </div>
    </div>
  );
}

export default reduxForm({
  form: EFormName.CLOSE_GROUP_MONEYBOX,
})(GroupInProgressMoneyboxComponent);
